import { ChecklistWidget } from '@/components'
import { Column, PageMainContent, Row } from '@/components/styled'
import { Breakpoint, CategoryId } from '@/enums'
import { isGenericCompanyDetails } from '@/modules/company/utils/isGenericCompanyDetails'
import { useCompany } from '@/services/company'
import { useLocationHome } from '@/services/location'
import { useIsMobile, useSearchParams } from '@/utils'
import { useOutletContext, useParams } from 'react-router-dom'
import { AddContractSection } from './components/AddContractSection'
import { CompanyDetailsChangedModal } from './components/CompanyDetailsChangedModal/CompanyDetailsChangedModal'
import { CoverageSection } from './components/CoverageSection'
import { MissingCoverageSection } from './components/MissingCoverageSection'
import { PageHeader } from './components/PageHeader'
import {
  findEssentialsWithoutRecommendations,
  LocationData,
  transform,
} from './helpers'
import { MiddleSectionColumn, StyledPageWidgetContainer } from './styles'
import { UserProfile } from '@/types/user-profile'
import { MissingCoverageChartWidget } from './components/MissingCoverage/Component'
import { OptimiseCoverCard } from '@/modules/yearlyCheck/components/OptimiseCoverCard'

export const LocationPage = () => {
  const { locationId = '' } = useParams()
  const { archived = 'false' } = useSearchParams()
  const { isMobile } = useIsMobile(Breakpoint.md)

  const archivedBoolean = archived === 'true'

  const { user } = useOutletContext<{ user: UserProfile }>()

  const { data, isLoading } = useLocationHome(
    { id: locationId, archived: archivedBoolean },
    {
      enabled: !!locationId,
      select: transform,
    },
  )

  const {
    data: locationActiveView,
    isLoading: isLoadingActiveView,
  } = useLocationHome(
    {
      id: locationId,
      archived: false,
    },
    {
      enabled: !!locationId,
      select: transform,
    },
  )

  const { data: companyData } = useCompany(data?.companyId || '', {
    enabled: Boolean(data?.companyId),
  })

  const essentialsWithoutRecommendations = findEssentialsWithoutRecommendations(
    locationActiveView as LocationData,
  )

  const isGenericLocation = isGenericCompanyDetails(
    data?.name || '',
    data?.address,
  )

  const showYearlyAssessment = data?.yearlyCheckInStatus === 'pending'

  return (
    <PageMainContent dark>
      <PageHeader
        userProfileData={user}
        isGenericLocation={isGenericLocation}
        name={data?.name || ''}
        address={data?.address}
      />
      <Row gap="32px">
        <MiddleSectionColumn>
          {showYearlyAssessment && (
            <OptimiseCoverCard locationId={locationId} />
          )}
          <MissingCoverageSection
            isLoading={isLoadingActiveView}
            missingCategories={essentialsWithoutRecommendations as CategoryId[]}
            recommendations={locationActiveView?.recommendations || []}
            locationId={locationId}
          />
          <AddContractSection />
          {isMobile && (
            <Column gap="24px">
              <ChecklistWidget
                data={locationActiveView?.riskChecklist}
                companyId={locationActiveView?.companyId}
                locationId={locationId}
              />
              <MissingCoverageChartWidget
                allInsurances={locationActiveView?.insurances || []}
                allRecommendations={locationActiveView?.recommendations || []}
                essentialCategoryIds={
                  locationActiveView?.essentialCategoryId || []
                }
                isLoading={isLoadingActiveView}
              />
            </Column>
          )}
          <CoverageSection
            insurances={data?.insurances || []}
            isLoading={isLoading}
            locationId={locationId}
            archived={archivedBoolean}
          />
        </MiddleSectionColumn>
        {!isMobile && (
          <StyledPageWidgetContainer>
            <ChecklistWidget
              data={locationActiveView?.riskChecklist}
              companyId={locationActiveView?.companyId}
              locationId={locationId}
            />
            <MissingCoverageChartWidget
              allInsurances={locationActiveView?.insurances || []}
              allRecommendations={locationActiveView?.recommendations || []}
              essentialCategoryIds={
                locationActiveView?.essentialCategoryId || []
              }
              isLoading={isLoadingActiveView}
            />
          </StyledPageWidgetContainer>
        )}
      </Row>
      {companyData?.poaResignReason && (
        <CompanyDetailsChangedModal
          poaResignReason={companyData.poaResignReason}
          companyId={companyData.companyId}
        />
      )}
    </PageMainContent>
  )
}
